'use client';

import Link from 'next/link';
import { useState } from 'react';
import { UserRound } from 'lucide-react';

import { CustomerAuthModal } from '@/components/customer-surface/CustomerAuthModal';
import { useCustomerSession } from '@/lib/customer-bridge';
import { cn } from '@/components/ui/utils';

/** Inline customer auth entry for public place surfaces. Guests can close it and
 * continue browsing; successful auth uses the existing OTP/passkey and claim flow. */
export function CustomerAccountEntry({
  accountHref,
  chow = false,
  compact = false,
}: {
  accountHref: string;
  chow?: boolean;
  /** Circular 44px control for a page header, like the mobile app. */
  compact?: boolean;
}) {
  const { status, data, refresh } = useCustomerSession();
  const [open, setOpen] = useState(false);
  const authenticated = status === 'authenticated';
  const initial = (data?.user?.name || data?.user?.email || '').trim().slice(0, 1).toUpperCase();

  const chowClass = compact
    ? 'gv-icon-btn'
    : 'gv-btn gv-btn--secondary gv-btn--md inline-flex';
  const legacyClass =
    'inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] px-4 text-sm font-semibold text-[var(--color-text-primary)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent)]';
  const className = chow ? chowClass : legacyClass;
  const label = authenticated ? 'Account' : 'Sign in';
  const content = compact ? (
    authenticated && initial ? (
      <span className="flex size-8 items-center justify-center rounded-full bg-[hsl(var(--chow-sunk))] text-[13px] font-semibold text-[hsl(var(--chow-ink))]">
        {initial}
      </span>
    ) : (
      <UserRound aria-hidden="true" className="size-5" />
    )
  ) : (
    <>
      <UserRound aria-hidden="true" className="size-4" />
      {label}
    </>
  );

  return (
    <>
      {authenticated ? (
        <Link href={accountHref} aria-label={compact ? 'Your account' : undefined} className={cn(className, compact && 'p-0')}>
          {content}
        </Link>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={compact ? 'Sign in' : undefined}
          className={cn(className, compact && 'p-0')}
        >
          {content}
        </button>
      )}
      <CustomerAuthModal open={open} onOpenChange={setOpen} onAuthenticated={() => void refresh()} chow={chow} />
    </>
  );
}
